/**
 * «کارتِ این رخداد جلوی چشم آمد» — مخرجِ آماری که صاحبِ رخداد می‌بیند
 * (مایگریشن 0077).
 *
 * قرینه‌اش روی موبایل `onViewableItemsChanged`ِ خودِ FlatList است؛ مرورگر
 * معادلش را در `IntersectionObserver` دارد: می‌گوید کدام عنصر واقعاً داخلِ
 * کادرِ دید آمد، بدونِ اینکه ما به هر اسکرول گوش بدهیم و مختصات حساب کنیم.
 *
 * چهار قید، همان‌ها که روی موبایل:
 *  • فقط کاربرِ واردشده — مهمان کلیدِ یکتای قابلِ اعتمادی ندارد (تصمیمِ
 *    مایگریشن 0070). صفحه‌ی کشف از 0062 برای مهمان هم باز است، پس این شرط
 *    واقعاً پیش می‌آید.
 *  • هر شناسه یک‌بار در هر نشست (`sent`)، و یک‌بار برای همیشه در خودِ
 *    دیتابیس (کلیدِ اصلیِ `event_viewers`).
 *  • دسته‌ای، با یک مکثِ کوتاه: اسکرولِ سریع از روی سی کارت باید یک درخواست
 *    بشود، نه سی تا.
 *  • شکستش بی‌صداست؛ شمردن نباید صفحه‌ای را که کاربر باز کرده خراب کند.
 */
import { useEffect, useRef } from 'react';
import { popularityRepo } from '@/core/data/popularityRepo';

/** مکثِ بعد از آخرین کارتِ دیده‌شده، قبل از فرستادنِ دسته. */
const FLUSH_MS = 1500;

/** چند درصدِ کارت باید دیده شود تا «دیده شد» حساب شود. */
const VISIBLE = 0.6;

export interface Impressions {
  /**
   * به هر کارتِ رخداد داده می‌شود: `ref={imp.watch(id)}`.
   *
   * خودِ تابع با شناسه کش می‌شود تا مرجعش بینِ رندرها ثابت بماند — وگرنه
   * React هر بار عنصر را از observer در می‌آورد و دوباره ثبت می‌کند.
   */
  watch: (id: string) => (el: HTMLElement | null) => void;
}

export function useImpressions(enabled: boolean): Impressions {
  const sent = useRef(new Set<string>());
  const pending = useRef(new Set<string>());
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const observer = useRef<IntersectionObserver | null>(null);
  const refs = useRef(new Map<string, (el: HTMLElement | null) => void>());
  /** عنصر → شناسه، چون خودِ callback فقط عنصر را می‌دهد. */
  const idOf = useRef(new WeakMap<Element, string>());

  useEffect(() => {
    if (!enabled || typeof IntersectionObserver === 'undefined') return undefined;

    const flush = () => {
      timer.current = null;
      if (pending.current.size === 0) return;
      const batch = [...pending.current];
      pending.current.clear();
      batch.forEach((id) => sent.current.add(id));
      popularityRepo.bumpImpressions(batch).catch(() => undefined);
    };

    observer.current = new IntersectionObserver((entries) => {
      let found = false;
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const id = idOf.current.get(entry.target);
        if (!id || sent.current.has(id) || pending.current.has(id)) continue;
        pending.current.add(id);
        found = true;
        // دیده شد؛ دیگر لازم نیست دنبالش باشیم.
        observer.current?.unobserve(entry.target);
      }
      if (found && timer.current === null) timer.current = setTimeout(flush, FLUSH_MS);
    }, { threshold: VISIBLE });

    return () => {
      if (timer.current) clearTimeout(timer.current);
      // آنچه دیده شده ولی هنوز نرفته، موقعِ ترکِ صفحه می‌رود — وگرنه
      // کاربری که سریع صفحه را عوض می‌کند هیچ‌وقت شمرده نمی‌شد.
      flush();
      observer.current?.disconnect();
      observer.current = null;
    };
  }, [enabled]);

  const watch = (id: string) => {
    const hit = refs.current.get(id);
    if (hit) return hit;
    const fn = (el: HTMLElement | null) => {
      if (!el || sent.current.has(id)) return;
      idOf.current.set(el, id);
      observer.current?.observe(el);
    };
    refs.current.set(id, fn);
    return fn;
  };

  return { watch };
}
